/**
 * PORTED — RO-3-00f / #44473.
 * Origin: `excellent/app/_home/portfolio-rollup.tsx`.
 *
 * Cause A rewrite: vault reads moved to `homePortfolio.portfolio`.
 */

import {
  Card,
  Ledger,
  MetricStrip,
  Section,
  SectionTitle,
  formatUsd,
  formatNumber,
  type DataTableColumn,
} from "@excellent/ui";
import { FunnelRegister } from "@excellent/ui/blocks/measured/funnel-register";
import { useApi } from "@excellent/ui/api";
import {
  homePortfolio,
  type HomePortfolioProperty,
} from "@excellent/ui/api/contracts/home-portfolio";

export function PortfolioRollup({ workspaceId }: { workspaceId: number }) {
  const { data, isLoading } = useApi(homePortfolio.portfolio, {
    query: { workspaceId },
  });

  if (isLoading || !data || !data.visible) return null;

  const columns: DataTableColumn<HomePortfolioProperty>[] = [
    {
      key: "name",
      header: "Property",
      width: "w-5/12",
      cellClassName: "truncate",
      render: (p) => p.name,
    },
    {
      key: "adoption",
      header: "Adoption",
      width: "w-3/12",
      cellClassName: "truncate",
      render: (p) => {
        const a = data.adoptionBySlug[p.slug];
        if (!a || a.value == null)
          return <span className="text-muted-foreground">—</span>;
        return (
          <span>
            <span className="tabular-nums">{formatMetric(a.value, a.unit)}</span>{" "}
            <span className="text-muted-foreground">{a.label.toLowerCase()}</span>
          </span>
        );
      },
    },
    {
      key: "revenue",
      header: "Revenue",
      width: "w-2/12",
      render: (p) => formatUsd(p.revenueMinor / 100),
    },
    {
      key: "paying",
      header: "Paying",
      width: "w-2/12",
      render: (p) => p.payingParties,
    },
  ];

  const strip = (
    <MetricStrip
      cells={[
        {
          key: "revenue",
          label: "Portfolio revenue",
          value: formatUsd(data.totalRevenueMinor / 100),
        },
        {
          key: "paying",
          label: data.payingCustomersLabel,
          value: String(data.payingCustomers),
        },
      ]}
    />
  );

  const funnel = data.funnel;

  return (
    <div className="flex flex-col gap-4">
      <Section
        label="Portfolio"
        aside="revenue is canonical-party-first — an org counts once"
      >
        <Ledger
          rows={data.properties}
          columns={columns}
          rowKey={(p) => p.slug}
          tableClassName="table-fixed"
          strip={strip}
        />
      </Section>

      {funnel &&
        (funnel.identified.touchedFrom > 0 || funnel.anonymousTop) && (
          <Card>
            <SectionTitle
              title={`Cross-property funnel · ${funnel.fromName} → ${funnel.toName}`}
              subtitle="Two registers on purpose: the anonymous top is an aggregate rate; the identified body is exact by shared identity."
            />
            <div className="flex flex-col gap-3 px-4 pb-4 pt-2">
              {funnel.anonymousTop && (
                <FunnelRegister
                  register="aggregate"
                  caption="anonymous — not joined to identities"
                >
                  <span className="font-medium tabular-nums">
                    {funnel.anonymousTop.visitors.toLocaleString()}
                  </span>
                  <span className="text-muted-foreground">visitors →</span>
                  <span className="font-medium tabular-nums">
                    {funnel.anonymousTop.signups.toLocaleString()}
                  </span>
                  <span className="text-muted-foreground">signups</span>
                  <span className="ml-auto font-medium tabular-nums">
                    {funnel.anonymousTop.signupRatePct}%
                  </span>
                </FunnelRegister>
              )}
              <FunnelRegister
                register="exact"
                caption="by shared identity (an org engaged both)"
              >
                <span className="font-medium tabular-nums">
                  {funnel.identified.touchedFrom}
                </span>
                <span className="text-muted-foreground">
                  engaged {funnel.fromName} →
                </span>
                <span className="font-medium tabular-nums">
                  {funnel.identified.both}
                </span>
                <span className="text-muted-foreground">
                  also paying {funnel.toName}
                </span>
                <span className="ml-auto font-medium tabular-nums">
                  {funnel.crossSellPct}% cross-sell
                </span>
              </FunnelRegister>
            </div>
          </Card>
        )}
    </div>
  );
}

function formatMetric(value: number, unit: string): string {
  switch (unit) {
    case "currency":
      return formatUsd(value);
    case "percent":
      return `${formatNumber(value)}%`;
    case "duration":
      return `${formatNumber(value)} min`;
    default:
      return formatNumber(value);
  }
}
